import { useAuthStore } from '../stores/authStore';
import { WS_CONFIG } from '../config/api';
export interface WebSocketMessage {
    type: string;
    [key: string]: any;
}

export interface ConnectionState {
    isConnected: boolean;
    isConnecting: boolean;
    reconnectAttempts: number;
    lastError?: string;
    lastUrl?: string;
    lastCloseCode?: number;
    lastCloseReason?: string;
}

class WebSocketService {
    private ws: WebSocket | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private listeners: Map<string, Set<(data: any) => void>> = new Map();
    private sentListeners: Set<(data: any) => void> = new Set();
    private isConnecting = false;
    private heartbeatInterval: any = null;
    private pongTimeout: any = null;
    private messageQueue: any[] = [];
    private connectionStateListeners: Set<(state: ConnectionState) => void> = new Set();
    private lastError: string | undefined;
    private lastUrl: string | undefined;
    private lastCloseCode: number | undefined;
    private lastCloseReason: string | undefined;
    private currentSessionId: string | null = null;
    private autoConnectEnabled = false;  // ✅ 默认为 false，由 useChat.ts 控制

    constructor() {
        // 构造函数不自动连接，由 useChat.ts 在用户登录后调用 connect()
    }

    // 设置当前会话ID，用于订阅文件状态
    setCurrentSessionId(sessionId: string | null) {
        this.currentSessionId = sessionId;
    }

    private notifyConnectionState(): void {
        const state = this.getConnectionInfo();
        this.connectionStateListeners.forEach(l => { try { l(state); } catch (e) { console.error(e); } });
    }
    

    private connect(token?: string): void {
        if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.CONNECTING)) return;

        this.isConnecting = true;
        this.notifyConnectionState();


        const effectiveToken = token ?? useAuthStore.getState().accessToken;
        if (!effectiveToken) {
            console.warn('⚠️ WebSocket 连接失败：没有认证 token');
            this.isConnecting = false;
            this.lastError = '未认证 - 请先登录';
            this.notifyConnectionState();
            return;
        }

        const wsUrl = `${WS_CONFIG.BASE_URL}/files/?token=${effectiveToken}`;
        this.lastUrl = wsUrl;
        console.log('🔗 Attempting WS connection to:', `${WS_CONFIG.BASE_URL}/files/`);

         try {
            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = () => {
                console.log('🌐 WS Connected');
                this.isConnecting = false;
                this.reconnectAttempts = 0;
                this.lastError = undefined;
                this.lastCloseCode = undefined;
                this.lastCloseReason = undefined;
                this.notifyConnectionState();
                this.startHeartbeat();
                this.flushQueue();

                // 连接建立后，如果已有当前会话ID，订阅相关文件
                if (this.currentSessionId) {
                    console.log('🎯 自动订阅当前会话文件');
                }
            };

            this.ws.onmessage = (event) => {
                try {
                    const data: WebSocketMessage = JSON.parse(event.data);
                    if (data.type === 'pong') return this.clearPongTimeout();
                    this.handleMessage(data);
                } catch (e) { console.error('WS Parse Error', e); }
            };

            this.ws.onclose = (e) => {
                this.isConnecting = false;
                this.ws = null;
                this.stopHeartbeat();
                this.lastCloseCode = e.code;
                this.lastCloseReason = e.reason || this.getCloseReasonText(e.code);
                console.log(`⚠️ WebSocket closed, code: ${e.code}, reason: ${this.lastCloseReason}`);
                if (e.code !== 1000 && this.autoConnectEnabled) this.autoReconnect();
                this.notifyConnectionState();
            };

            this.ws.onerror = (e) => {
                console.error('❌ WebSocket error:', e);
                this.lastError = `Connection error (see console)`;
                this.notifyConnectionState();
            };
        } catch (e) {
            console.error('❌ WebSocket initialization failed:', e);
            this.isConnecting = false;
            this.lastError = 'Initialization failed';
            this.notifyConnectionState();
        }
    }
    private autoReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('❌ Max reconnect attempts reached');
            return;
        }
        this.reconnectAttempts++;
        const delay = Math.min(500 * Math.pow(2, this.reconnectAttempts - 1), 10000);
        console.log(`🔄 Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
        setTimeout(() => this.connect(), delay);
    }

    private startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            if (this.isConnected()) {
                this.send({ type: 'ping' });
                this.pongTimeout = setTimeout(() => {
                    console.warn('⚠️ Pong timeout, closing connection');
                    this.ws?.close();
                }, 3000);
            }
        }, 5000);
    }

    private clearPongTimeout() {
        if (this.pongTimeout) { clearTimeout(this.pongTimeout); this.pongTimeout = null; }
    }

    private stopHeartbeat() {
        if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
        this.clearPongTimeout();
    }

    private flushQueue() {
        while (this.messageQueue.length > 0 && this.isConnected()) {
            this.send(this.messageQueue.shift());
        }
    }

    private handleMessage(data: WebSocketMessage) {
        [data.type, '*'].forEach(t => this.listeners.get(t)?.forEach(cb => cb(data)));
    }

    public isConnected(): boolean { 
        return this.ws?.readyState === WebSocket.OPEN; 
    }

    public send(data: any): boolean {
        if (this.isConnected()) {
            this.ws!.send(JSON.stringify(data));
            this.notifySentListeners(data);
            return true;
        }
        this.messageQueue.push(data);
        console.log('📭 Message queued (not connected):', data.type);
        return false;
    }

    private notifySentListeners(data: any): void {
        this.sentListeners.forEach(cb => {
            try {
                cb(data);
            } catch (e) {
                console.error('Sent listener error:', e);
            }
        });
    }

    public onMessageSent(callback: (data: any) => void): () => void {
        this.sentListeners.add(callback);
        return () => this.sentListeners.delete(callback);
    }

    public subscribe(type: string, callback: (data: any) => void): () => void {
        if (!this.listeners.has(type)) this.listeners.set(type, new Set());
        this.listeners.get(type)!.add(callback);
        return () => { this.listeners.get(type)?.delete(callback); };
    }

    public onStateChange(callback: (state: ConnectionState) => void) {
        this.connectionStateListeners.add(callback);
        callback(this.getConnectionInfo());
        return () => this.connectionStateListeners.delete(callback);
    }

    public getConnectionInfo(): ConnectionState {
        return { isConnected: this.isConnected(), isConnecting: this.isConnecting,
                 reconnectAttempts: this.reconnectAttempts, lastError: this.lastError,
                 lastUrl: this.lastUrl, lastCloseCode: this.lastCloseCode, lastCloseReason: this.lastCloseReason };
    }

    private getCloseReasonText(code: number): string {
        const reasons: Record<number, string> = {
            1000: 'Normal closure',
            1001: 'Going away',
            1002: 'Protocol error',
            1003: 'Unsupported data',
            1005: 'No status received',
            1006: 'Abnormal closure (server unreachable or rejected)',
            1008: 'Policy violation',
            1009: 'Message too big',
            1011: 'Server error',
            1015: 'TLS handshake failure'
        };
        return reasons[code] || 'Unknown';
    }

    public forceReconnect(token?: string) {
        console.log('🔄 Force reconnect requested');
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close();
        }
        this.reconnectAttempts = 0;
        this.lastError = undefined;
        this.connect(token);
    }
    
    public close() {
        console.log('🛑 Closing WebSocket connection');
        this.stopHeartbeat();
        this.autoConnectEnabled = false;
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close();
            this.ws = null;
        }
        this.isConnecting = false;
        this.lastError = undefined;
        this.notifyConnectionState();
    }
    
    // 启用自动重连
    public enableAutoConnect(enabled: boolean) {
        this.autoConnectEnabled = enabled;
        if (enabled && !this.isConnected() && !this.isConnecting) {
            this.connect();
        }
    }
    
}

export const websocketService = new WebSocketService();
export interface FileStatusUpdate {
    type: 'file_status_update';
    file_id: string;
    session_id?: string;      // ✅ 必须：用于前端校验是否为当前活跃会话
    file_name?: string;
    status: string;
    status_message?: string;
    progress?: number;
    timestamp?: string;
    // 自动确认模式标志（后端每条推送都带）：true 时前端跳过人工确认交互
    auto_confirm?: boolean;
    // 提取结果快照（自动确认模式下随 ntrk_analysis 推送下发，只读展示）
    extraction_result?: {
        file_name?: string;
        variants: Array<{
            ntrk_variant?: string;
            breakpoint?: string;
            abundance?: string;
            partner_gene?: string;
            ntrk_transcript_info?: string;
        }>;
        tumor_type?: string;
        co_variants?: string;
    };


    // ✅ 关键：汇总信息快照
    file_summary?: {
        total: number;
        confirmed: number;
        analyzed: number;
        chat_ready: boolean; // 后端根据所有文件状态计算出的"全就绪"标志
    };
}